
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { ArrowRight, Brain, Cog, TrendingUp, Zap } from "lucide-react";
import { ConsultationForm } from "./ConsultationForm";
import { useEffect, useState } from "react";

interface HeroProps {
  homepageData?: {
    heroHeadline?: string;
    heroSubheadline?: string;
    heroCtaButtonText?: string;
    heroCtaButtonLink?: string;
  };
}

export const Hero = ({ homepageData }: HeroProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProgress(100);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="pt-32 pb-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-4xl">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-light text-gray-900 leading-tight mb-8">
            {"Bespoke Web & Digital Solutions for Ambitious Brands"}
          </h2>

          
          <p className="text-xl text-gray-700 mb-10 max-w-2xl">
            {homepageData?.heroSubheadline || (
              <>
                Orizis delivers custom <a href="/blog/understanding-ai-automation-processes" className="text-violet-600 hover:text-violet-700 underline">AI-driven automation solutions</a> for UK businesses—boosting productivity, reducing costs, and unlocking scalable growth without increasing headcount.
              </>
            )}
          </p>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-transparent border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-3 text-sm font-medium transition-all duration-300 group">
                {homepageData?.heroCtaButtonText || "GET YOUR FREE AI AUTOMATION CONSULTATION"}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Button>
            </DialogTrigger>
            <ConsultationForm />
          </Dialog>
        </div>
        
        <div className="mt-12">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
            <div className="max-w-2xl mx-auto">
              {/* Process Steps in One Line */}
              <div className="flex items-center justify-center space-x-8 mb-6">
                <div className="flex flex-col items-center group">
                  <div className="w-12 h-12 bg-violet-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Brain className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                </div>

                <ArrowRight className="w-5 h-5 text-gray-400" aria-hidden="true" />

                <div className="flex flex-col items-center group">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Cog className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                </div>

                <ArrowRight className="w-5 h-5 text-gray-400" aria-hidden="true" />

                <div className="flex flex-col items-center group">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Zap className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                </div>

                <ArrowRight className="w-5 h-5 text-gray-400" aria-hidden="true" />

                <div className="flex flex-col items-center group">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                </div>
              </div>

              {/* Process Flow Visualization */}
              <div className="relative">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <div className="flex items-center justify-start ml-8">
                    <div className="flex items-center justify-between w-full max-w-lg">
                      <div className="text-center">
                        <div className="w-2 h-2 bg-violet-500 rounded-full mx-auto mb-2"></div>
                        <span className="text-xs text-gray-700">Manual Process</span>
                      </div>
                      
                      <div className="flex-1 mx-4 relative">
                        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-violet-500 to-green-500 rounded-full transition-all duration-2000 ease-out"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mx-auto mb-2"></div>
                        <span className="text-xs text-gray-700">AI Automated</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <span className="text-sm font-medium text-gray-900">Transform Your Business Operations</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
